<template>
  <div class="page-wrap">
    <PageHeader :title="currentCategory.label" />

    <!-- 검색 + 새로고침 (전 탭 공통) -->
    <div class="browse-toolbar">
      <v-text-field
        v-model="search"
        placeholder="학교명 / 학교코드 검색"
        prepend-inner-icon="mdi-magnify"
        density="compact"
        variant="outlined"
        hide-details
        clearable
        class="search-field"
      />
      <v-select
        v-if="showPlanFilter"
        v-model="planFilter"
        :items="planOptions"
        item-title="title"
        item-value="value"
        density="compact"
        variant="outlined"
        hide-details
        class="plan-filter-field"
      />
      <v-spacer />
      <span class="result-count">
        <b>{{ filteredList.length }}</b>개 학교
      </span>
      <v-btn
        variant="outlined"
        color="primary"
        size="small"
        :loading="loading"
        @click="fetchAll(true)"
      >
        새로고침
      </v-btn>
    </div>

    <div class="table-block">
    <!-- 유료 탭 = 학교 관리와 완전히 동일한 데이터라, 그 화면(allschool.vue)을
         그대로 재사용한다. 등록/삭제 등 기능까지 항상 100% 동일하게 유지된다. -->
    <allschool v-if="category === 'paid'" :tab="1" :schools="paidFilteredList" />

    <!-- :key="loaded" — items가 비어있는 채로 한 번 마운트된 v-data-table은 이후
         실제 데이터가 채워지고 page를 복원해도 1페이지에 고정된 채 반응하지 않는
         Vuetify 쪽 현상이 있어, 데이터가 준비된 시점에 아예 새 인스턴스로 다시
         마운트되게 한다. -->
    <DataTable
      v-else
      :key="loaded"
      class="browse-table"
      fill-height
      :headers="activeHeaders"
      :items="filteredList"
      :loading="loading"
      row-key="schoolCode"
      clickable
      v-model:page="page"
      :scroll-key="`schoolBrowse-${category}`"
      @row-click="openDetail"
    >
      <!-- 유료 탭(allschool.vue)과 동일하게, 건수/버튼도 테이블 박스 "안쪽" 툴바로 표기 -->
      <template #toolbar>
        <span class="table-count"><b>{{ pad(filteredList.length) }}</b>건</span>
        <v-spacer />
        <!-- 연락처 소스(전체/LMS 인증/수동 등록)를 골라서 다운로드 -->
        <v-menu v-if="showContactDownload" location="bottom end">
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="outlined"
              size="small"
              color="success"
              append-icon="mdi-menu-down"
            >
              연락처 다운로드
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item @click="downloadContacts('all')">
              <v-list-item-title>전체 (LMS 인증 + 수동 등록)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadContacts('lms')">
              <v-list-item-title>LMS 인증 연락처만</v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadContacts('manual')">
              <v-list-item-title>수동 등록 연락처만</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template #[`item.schoolName`]="{ item }">
        <span class="school-name" :title="item.schoolName">{{ item.schoolName }}</span>
      </template>

      <template #[`item.contractStudentNumber`]="{ item }">
        <span :class="item.contractYear && item.contractYear.length ? '' : 'empty-text'">
          {{ getcontractstudentnumber(item.contractYear) }}
        </span>
      </template>

      <template #[`item.office`]="{ item }">
        <span :class="item.office ? '' : 'empty-text'">{{ item.office || "-" }}</span>
      </template>

      <template #[`item.payPlan`]="{ item }">
        <span :class="item.payPlan ? '' : 'empty-text'">{{ item.payPlan || "-" }}</span>
      </template>

      <template #[`item.memo`]="{ item }">
        <span class="memo-text" :class="item.memo ? '' : 'empty-text'" :title="item.memo">
          {{ item.memo || "-" }}
        </span>
      </template>

      <template #[`item.startDate`]="{ item }">
        <span :class="item.startDate ? 'date-text' : 'empty-text'">
          {{ formatDate(item.startDate) }}
        </span>
      </template>

      <template #[`item.endDate`]="{ item }">
        <span :class="item.endDate ? 'date-text' : 'empty-text'">
          {{ formatDate(item.endDate) }}
        </span>
      </template>

      <template #[`item.periodStart`]="{ item }">
        <span :class="item.periodStart ? 'date-text' : 'empty-text'">
          {{ formatDate(item.periodStart) }}
        </span>
      </template>

      <template #[`item.periodEnd`]="{ item }">
        <span :class="item.periodEnd ? 'date-text' : 'empty-text'">
          {{ formatDate(item.periodEnd) }}
        </span>
      </template>

      <template #[`item.createdAt`]="{ item }">
        <span :class="item.createdAt ? 'date-text' : 'empty-text'">
          {{ formatDate(item.createdAt) }}
        </span>
      </template>

      <template #empty>
        <EmptyState
          :title="`${currentCategory.label} 학교가 없습니다`"
          :description="emptyDescription"
        />
      </template>
    </DataTable>
    </div>
  </div>
</template>

<script>
import { getschoollist } from "@/api/user-api-list";
import { getSchoolCreate } from "@/api/guest-api-list";
import { getLastFromPath } from "@/router";
import PageHeader from "@/components/ui/PageHeader.vue";
import DataTable from "@/components/ui/DataTable.vue";
import EmptyState from "@/components/ui/EmptyState.vue";
import allschool from "@/components/userAccountManage/schoolManage/allschool.vue";
import { instanceAuth } from "@/api/url";
import { resolveCategory } from "@/constants/schoolType";

const CATEGORIES = [
  { key: "paid", label: "유료" },
  { key: "free", label: "무료" },
  { key: "training", label: "연수" },
  { key: "supporters", label: "서포터즈" },
  { key: "ai", label: "AI미래교육연구회" },
  { key: "expo", label: "박람회" },
];

// 특수 분류 탭 — 유료/체험이 섞일 수 있어 공용 헤더를 쓴다.
const SPECIAL_CATEGORIES = ["training", "supporters", "ai", "expo"];

// 서포터즈/AI미래교육연구회 — 실제로는 거의 유료(schoolLevel=user) 학교라
// 유료 탭(allschool.vue)과 동일한 컬럼 구성을 쓴다. 다만 이 화면은 조회 전용이라
// "삭제" 대신 "생성일"을 마지막 컬럼으로 둔다.
const PAID_STYLE_CATEGORIES = ["supporters", "ai"];

// 총 건수 + 연락처 다운로드를 노출할 탭 — 전 탭 지원 (유료 탭은 allschool.vue가 자체 노출)
const CONTACT_DOWNLOAD_CATEGORIES = ["free", "training", "supporters", "ai", "expo"];

export default {
  name: "SchoolBrowseView",
  components: { PageHeader, DataTable, EmptyState, allschool },
  data() {
    return {
      // 검색어/페이지 복원은 "상세 페이지에서 뒤로가기로 돌아온 경우"에만
      // restoreListState()가 수행한다 — 그 외(새로고침/다른 탭 유입)는 빈 상태로 시작.
      search: "",
      // 유료/서포터즈/AI미래교육연구회/박람회 탭에서만 노출되는 플랜 필터
      planFilter: "",
      planOptions: [
        // value는 DB schools.payPlan 실제 저장값 기준 (s/p/m/sm/pm — 실측 확인).
        // 'general'/'premium'은 DB에 존재하지 않는 표기라 쓰면 필터가 항상 0건이 된다.
        { title: "플랜", value: "" },
        { title: "s", value: "s" },
        { title: "p", value: "p" },
        { title: "m", value: "m" },
        { title: "sm", value: "sm" },
        { title: "pm", value: "pm" },
      ],
      loading: false,
      paidSchools: [],
      guestSchools: [],
      loaded: false,
      // 상세 페이지 갔다가 뒤로가기 했을 때 보던 페이지 그대로 유지하기 위해
      // 탭(category)별로 sessionStorage에 저장해둔다.
      page: 1,
      // restoreListState() 진행 중엔 search watch의 "페이지 1로 리셋"을 건너뛰기
      // 위한 플래그 (자세한 이유는 restoreListState 주석 참고)
      _restoringState: false,

      // 유료 탭은 allschool.vue를 그대로 재사용하므로 별도 헤더가 필요 없다.
      guestHeaders: [
        { title: "No", key: "no", sortable: false, align: "center", width: 60 },
        { title: "학교명", key: "schoolName", align: "center", width: 160 },
        { title: "코드", key: "schoolCode", align: "center", width: 90 },
        { title: "체험 시작일", key: "startDate", align: "center", width: 110 },
        { title: "체험 만료일", key: "endDate", align: "center", width: 110 },
        { title: "생성일", key: "createdAt", align: "center", width: 110 },
      ],
      // 연수 탭 — 유료(user)/체험(guest)이 섞이므로 양쪽에 다 있는 항목만 노출
      specialHeaders: [
        { title: "No", key: "no", sortable: false, align: "center", width: 60 },
        { title: "학교명", key: "schoolName", align: "center", width: 160 },
        { title: "코드", key: "schoolCode", align: "center", width: 90 },
        { title: "시작일", key: "periodStart", align: "center", width: 110 },
        { title: "만료일", key: "periodEnd", align: "center", width: 110 },
        { title: "생성일", key: "createdAt", align: "center", width: 110 },
      ],
      // 서포터즈/AI미래교육연구회 — 유료 탭(allschool.vue)과 동일한 컬럼 구성.
      // periodStart/periodEnd는 fetchAll()에서 유료=contractStartDate/EndDate,
      // 체험=startDate/endDate로 이미 매핑해 둔 공용 필드를 그대로 쓴다.
      // 생성일은 학교 상세 페이지의 "계정 생성일"에서 확인 가능하므로, 여기서는
      // 대신 메모를 보여준다.
      paidStyleHeaders: [
        { title: "No", key: "no", sortable: false, align: "center", width: 60 },
        { title: "교육청", key: "office", align: "center", width: 140 },
        { title: "학교 이름", key: "schoolName", align: "center", width: 220 },
        { title: "코드", key: "schoolCode", align: "center", width: 120 },
        { title: "계약 학생 수", key: "contractStudentNumber", sortable: false, align: "center", width: 110 },
        { title: "플랜", key: "payPlan", align: "center", width: 90 },
        { title: "계약 시작일", key: "periodStart", align: "center", width: 120 },
        { title: "계약 만료일", key: "periodEnd", align: "center", width: 120 },
        { title: "메모", key: "memo", align: "center", width: 180 },
      ],
      // 박람회 — 무료 탭과 동일한 표기(체험 시작일/체험 만료일)를 쓰되, 유료/체험이
      // 섞일 수 있어 데이터는 fetchAll()이 만들어 둔 공용 periodStart/periodEnd
      // 필드를 사용한다 (연수 탭과 같은 이유 — guestHeaders의 startDate/endDate는
      // 체험 학교 원본 필드라 유료 학교엔 없다).
      expoHeaders: [
        { title: "No", key: "no", sortable: false, align: "center", width: 60 },
        { title: "학교명", key: "schoolName", align: "center", width: 160 },
        { title: "코드", key: "schoolCode", align: "center", width: 90 },
        { title: "체험 시작일", key: "periodStart", align: "center", width: 110 },
        { title: "체험 만료일", key: "periodEnd", align: "center", width: 110 },
        { title: "생성일", key: "createdAt", align: "center", width: 110 },
      ],
    };
  },
  computed: {
    category() {
      const param = this.$route.params.category;
      return CATEGORIES.some((c) => c.key === param) ? param : "paid";
    },
    currentCategory() {
      return CATEGORIES.find((c) => c.key === this.category);
    },
    activeHeaders() {
      if (this.category === "expo") return this.expoHeaders;
      if (PAID_STYLE_CATEGORIES.includes(this.category)) return this.paidStyleHeaders;
      if (this.category === "training") return this.specialHeaders;
      return this.guestHeaders;
    },
    // 플랜 필터 드롭다운은 유료/서포터즈/AI미래교육연구회/박람회에서만 노출
    showPlanFilter() {
      return this.category === "paid" || PAID_STYLE_CATEGORIES.includes(this.category);
    },
    // 카테고리별 목록 (검색 전)
    // 분류 기준은 type 우선 — 특수 분류(연수/서포터즈/AI/박람회)에 해당하면
    // 유료(user)/체험(guest) 여부와 무관하게 그 탭으로만 들어간다.
    // 나머지는 schoolLevel 로 유료/무료를 가른다. (한 학교는 정확히 한 탭에만 노출)
    listByCategory() {
      const map = {};
      for (const cat of CATEGORIES) map[cat.key] = [];
      for (const school of [...this.paidSchools, ...this.guestSchools]) {
        map[resolveCategory(school)].push(school);
      }
      for (const key of Object.keys(map)) {
        map[key] = this.sortByCreated(map[key]);
      }
      return map;
    },
    filteredList() {
      const list = this.listByCategory[this.category] || [];
      const q = (this.search || "").trim().toLowerCase();
      let filtered = q
        ? list.filter(
            (s) =>
              (s.schoolName || "").toLowerCase().includes(q) ||
              (s.schoolCode || "").toLowerCase().includes(q)
          )
        : list;
      if (this.showPlanFilter && this.planFilter) {
        filtered = filtered.filter((s) => s.payPlan === this.planFilter);
      }
      // No 컬럼용 순번 — 학교 관리(allschool.vue)와 동일하게 화면에 보이는 순서 기준
      return filtered.map((s, idx) => ({ ...s, no: idx + 1 }));
    },
    // 유료 탭 전용 — allschool.vue에 그대로 넘길 목록 (no 필드는 allschool이 자체 부여)
    paidFilteredList() {
      const list = this.listByCategory.paid || [];
      const q = (this.search || "").trim().toLowerCase();
      let filtered = q
        ? list.filter(
            (s) =>
              (s.schoolName || "").toLowerCase().includes(q) ||
              (s.schoolCode || "").toLowerCase().includes(q)
          )
        : list;
      if (this.planFilter) {
        filtered = filtered.filter((s) => s.payPlan === this.planFilter);
      }
      return filtered;
    },
    showContactDownload() {
      return CONTACT_DOWNLOAD_CATEGORIES.includes(this.category);
    },
    emptyDescription() {
      if (SPECIAL_CATEGORIES.includes(this.category)) {
        return "해당 분류로 등록된 학교가 아직 없습니다. 학교 등록 시 분류를 지정하거나, 학교 상세의 '분류' 항목에서 변경하면 여기에 표시됩니다.";
      }
      return "조건에 해당하는 학교가 없습니다.";
    },
    pageStorageKey() {
      return `schoolBrowsePage:${this.category}`;
    },
    searchStorageKey() {
      return `schoolBrowseSearch:${this.category}`;
    },
    // DataTable.vue가 scroll-key prop으로부터 만드는 저장 키와 동일한 규칙
    // (schoolBrowse-${category}) — 상세 페이지에서 온 게 아닐 때 미리 지워서
    // 예전 스크롤이 복원되지 않게 하는 데 쓴다.
    scrollStorageKey() {
      return `dataTableScroll:schoolBrowse-${this.category}`;
    },
  },
  async created() {
    // 상세 페이지에서 뒤로가기로 돌아온 경우에만 페이지/검색/스크롤을 복원한다.
    // 새로고침하거나 다른 탭(카테고리)에서 넘어온 경우엔 항상 맨 위 1페이지로.
    const cameFromDetail = /^\/(user\/school-manage|guest\/guest-manage)\//.test(
      getLastFromPath() || ""
    );
    if (!cameFromDetail) {
      // DataTable이 마운트 시점에 예전 스크롤을 복원하지 않도록 미리 지운다.
      sessionStorage.removeItem(this.scrollStorageKey);
    }
    // fetch 완료 전(items가 비어있는 동안) page를 먼저 복원해두면, v-data-table이
    // "0건인데 2페이지"인 순간을 거치면서 내부적으로 1페이지로 고정돼버리고
    // 이후 실제 데이터가 채워져도 복원한 페이지로 다시 안 돌아오는 문제가 있었다.
    // 데이터가 다 채워진 뒤에 복원해야 제대로 그 페이지로 보여진다.
    await this.fetchAll();
    // 사이드바 전역 검색에서 ?q=코드 로 넘어온 경우 — 상세에서 돌아온 복원보다 우선한다.
    if (this.$route.query.q) {
      this.search = String(this.$route.query.q);
      this.planFilter = "";
      this.page = 1;
    } else if (cameFromDetail) {
      this.restoreListState();
    }
  },
  watch: {
    // 화면 인스턴스를 재사용한 채 카테고리(탭)나 검색 쿼리만 바뀌는 경우
    // (사이드바 목록 클릭, 전역 검색 결과 이동 등) 를 한 곳에서 함께 처리한다.
    // 두 값이 동시에 바뀌는 경우가 있어(전역 검색으로 다른 카테고리+검색어로
    // 한 번에 이동) 별도 watcher 두 개로 나누면 실행 순서를 보장할 수 없어
    // 하나의 $route watcher로 묶었다.
    $route(to, from) {
      const categoryChanged = to.params.category !== from.params.category;
      const q = to.query.q;
      const queryChanged = q !== from.query.q;
      if (categoryChanged) {
        // 상세에서 돌아온 게 아니므로 항상 맨 위 1페이지로 새로 시작한다.
        // (스크롤을 맨 위로 되돌리는 건 DataTable.vue의 scroll-key watch가 처리)
        this.search = "";
        this.planFilter = "";
        this.page = 1;
      }
      if (q && (queryChanged || categoryChanged)) {
        this.search = String(q);
        this.planFilter = "";
        this.page = 1;
      }
    },
    search(val) {
      // v-text-field의 X(clear)는 null을 주는데, 그대로 저장하면 문자열 "null"이
      // 기록돼 뒤로가기 복원 때 검색어 "null"로 필터되는 표시 이상이 생긴다 (qa-feedback-1020)
      sessionStorage.setItem(this.searchStorageKey, val || "");
      // 검색으로 목록이 줄어들면 이전 페이지가 없어질 수 있으니 1페이지로 —
      // 단, restoreListState()가 search/page를 순서대로 복원하는 도중에는
      // (watch가 비동기로 배치 실행되어) 이 리셋이 나중에 실행되며 복원된
      // page 값을 도로 1로 덮어써버릴 수 있어 건너뛴다.
      if (this._restoringState) return;
      this.page = 1;
    },
    page(val) {
      sessionStorage.setItem(this.pageStorageKey, String(val));
    },
  },
  methods: {
    restoreListState() {
      this._restoringState = true;
      const savedSearch = sessionStorage.getItem(this.searchStorageKey);
      // 과거 버그로 저장된 "null" 문자열 방어 (qa-feedback-1020)
      if (savedSearch && savedSearch !== "null") this.search = savedSearch;
      const saved = parseInt(sessionStorage.getItem(this.pageStorageKey), 10);
      this.page = Number.isFinite(saved) && saved > 0 ? saved : 1;
      // watch는 "pre" 플러시라 nextTick보다 먼저 실행되므로, 여기서 플래그를
      // 꺼도 위에서 예약된 search watch에는 이미 true로 보인 뒤다.
      this.$nextTick(() => {
        this._restoringState = false;
      });
    },
    async fetchAll(force = false) {
      if (this.loaded && !force) return;
      this.loading = true;
      try {
        const [paidRes, guestRes] = await Promise.all([
          getschoollist(),
          getSchoolCreate(),
        ]);
        // 유료는 contractStartDate/EndDate, 체험은 startDate/endDate 로 기간 필드명이
        // 다르므로 두 탭이 섞이는 특수 분류용으로 공용 기간 필드를 만들어 둔다.
        // schoolName: 원본 API는 지역이 빠진 짧은 이름만 주므로, 학교 관리(allschool.vue)와
        // 동일하게 "지역 + 이름 + 초등학교" 형태로 합쳐서 보여준다 (체험 학교는 원본에 이미
        // 완전한 이름이 들어있어 그대로 둔다).
        this.paidSchools = (paidRes?.data?.data ?? []).map((s) => ({
          ...s,
          schoolLevel: s.schoolLevel ?? "user",
          schoolName: `${s.location ?? ""} ${s.schoolName}초등학교`.trim(),
          periodStart: s.contractStartDate,
          periodEnd: s.contractEndDate,
        }));
        this.guestSchools = (guestRes?.data?.data ?? []).map((s) => ({
          ...s,
          schoolLevel: s.schoolLevel ?? "guest",
          periodStart: s.startDate,
          periodEnd: s.endDate,
        }));
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },
    openDetail(item) {
      // 특수 분류 탭에는 유료/체험이 섞여 있으므로 탭이 아닌 학교 등급으로 분기한다.
      if (item.schoolLevel === "guest") {
        this.$router.push(`/guest/guest-manage/${item.schoolCode}`);
      } else {
        this.$router.push(`/user/school-manage/${item.schoolCode}`);
      }
    },
    sortByCreated(list) {
      return [...list].sort((a, b) => this.createdAtMillis(b) - this.createdAtMillis(a));
    },
    // 학교 생성 시각 추출. createdAt → _id timestamp 순으로 fallback
    // (학교 등록/수정 allschool.vue와 동일한 규칙)
    createdAtMillis(school) {
      if (school?.createdAt) {
        const t = new Date(school.createdAt).getTime();
        if (!Number.isNaN(t)) return t;
      }
      const id = school?._id;
      if (typeof id === "string" && id.length >= 8) {
        const secs = parseInt(id.substring(0, 8), 16);
        if (Number.isFinite(secs)) return secs * 1000;
      }
      return 0;
    },
    // '-' 를 값으로 갖고 있는 상태 필드는 미설정과 동일하게 본다.
    statusOf(value) {
      const status = (value ?? "").trim();
      return status === "-" ? "" : status;
    },
    // GUEST 등록/수정(SchoolTableComponent.vue)과 동일하게 슬래시 표기 + 빈값은 "-"
    formatDate(value) {
      if (!value) return "-";
      const d = new Date(String(value).replace(/\//g, "-"));
      if (isNaN(d.getTime())) return String(value);
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${d.getFullYear()}/${mm}/${dd}`;
    },
    // 학교 관리 allschool.vue와 동일한 연락처 다운로드 (전체 학교 연락처, 탭 무관 공통 엔드포인트)
    async downloadContacts(source = "all") {
      // url.js의 baseURL 설정을 따라간다 (로컬/운영 전환 시 하드코딩 수정 불필요)
      // 현재 탭(category)의 학교들만 내려받는다 — 백엔드가 탭 분류와 동일 기준으로 필터
      // source: all(전체) / lms(LMS 인증 연락처만) / manual(수동 등록 연락처만)
      window.open(
        `${instanceAuth.defaults.baseURL}/schools/download/contact?category=${this.category}&source=${source}`
      );
    },
    pad(num) {
      return num < 10 ? num.toString().padStart(2, "0") : num;
    },
    // allschool.vue와 동일한 규칙 — contractYear에서 올해 연도 항목의 계약 학생수.
    getcontractstudentnumber(data) {
      if (!data || data.length === 0) return "-";
      const year = new Date().getFullYear();
      for (let i = 0; i < data.length; i++) {
        if (data[i].year != year) continue;
        return data[i].contractStudentNumber;
      }
      return "-";
    },
  },
};
</script>

<style scoped>
/* 페이지 전체 스크롤 대신 목록(table-block) 안에서만 스크롤되도록 —
   DefaultLayout의 v-main(scrollable) + .app-content(height:100%) 위에서
   이 페이지만 height:100%로 고정하고 나머지는 내부 flex 자식에게 위임한다.
   (학교 등록/수정 SchoolManageComponent.vue와 동일 패턴) */
.page-wrap {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.page-wrap :deep(.page-header) {
  margin-bottom: 0;
}

.table-count {
  font-size: var(--font-small-size);
  color: var(--text-secondary);
}

.table-block {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 테이블 공통 — 유료 탭(allschool.vue)과 동일하게 첫 컬럼 고정/구분선 없이
   그냥 가로 스크롤만 (nowrap) */
.browse-table :deep(th),
.browse-table :deep(td) {
  white-space: nowrap;
  vertical-align: middle;
}
/* 기본(table-layout:auto)은 실제 렌더 폭이 지정한 width 비율대로 균등하게
   늘어나긴 하지만, 콘텐츠에 따라 매 렌더마다 살짝씩 달라질 수 있어 컬럼
   간격이 고르지 않아 보일 수 있다. width를 그대로 고정폭으로 써서 항상
   지정한 비율 그대로 렌더링되게 한다. */
.browse-table :deep(table) {
  table-layout: fixed;
}
.browse-table :deep(.v-table__wrapper) {
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--border-default) transparent;
}
.browse-table :deep(.v-table__wrapper)::-webkit-scrollbar {
  height: 8px;
}
.browse-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb {
  background: var(--border-default);
  border-radius: 4px;
}

/* 헤더 라벨은 컬럼 정렬(align)과 무관하게 항상 가운데.
   본문 셀은 기존 정렬(학교명/교육청/메모는 좌측)을 유지해 긴 텍스트 가독성을 지킨다. */
.browse-table :deep(thead th) {
  text-align: center !important;
}
.browse-table :deep(thead th .v-data-table-header__content) {
  justify-content: center;
}

.browse-toolbar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex: 0 0 auto;
}

.search-field {
  flex: 0 1 260px;
  min-width: 180px;
}
.plan-filter-field {
  flex: 0 0 120px;
  min-width: 100px;
}
.result-count {
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.result-count b {
  color: var(--text-primary);
}

/* 텍스트 컬럼은 셀 폭이 남아도 항상 좌측에서 시작 — 행마다 시작점이 흔들리지 않게 한다. */
.school-name,
.office-text {
  display: block;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
}
.school-name {
  font-weight: 600;
  color: var(--text-primary);
  display: inline-block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
.memo-text {
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  color: var(--text-secondary);
}
.office-text {
  color: var(--text-secondary);
}
.date-text {
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}
.empty-text {
  color: var(--text-tertiary);
}
</style>
